import React, { useEffect, useRef, memo, useState } from 'react'
import { Box, Flex, PseudoBox, Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/core'
import EmojiPicker, { EmojiStyle } from 'emoji-picker-react'
import { PlusIcon } from 'lucide-react'
import { useLockScrollOnElementRole } from 'hooks/useLockScrollOnElementRole'

interface IProps {
  onEmojiSelect: (emoji: string) => void
  openFullPicker: () => void
  isFullPickerOpen: boolean
}

export const ReactionsPicker = memo(({ onEmojiSelect, openFullPicker, isFullPickerOpen }: IProps) => {
  const reactionEmojis = ['😀', '😂', '😍', '😢', '😡', '👍']
  const pickerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const [isEmojiPickerReady, setIsEmojiPickerReady] = useState(false)

  useLockScrollOnElementRole('scrollableDiv', 'dialog')

  useEffect(() => {
    if (isFullPickerOpen) {
      const timer = setTimeout(() => {
        setIsEmojiPickerReady(true)
      }, 100)
      return () => clearTimeout(timer)
    } else {
      setIsEmojiPickerReady(false)
    }
  }, [isFullPickerOpen])

  const handleEmojiClick = (emojiObject: any) => {
    if (emojiObject && emojiObject.emoji) {
      onEmojiSelect(emojiObject.emoji)
      const eventClose = new CustomEvent('closeAllReactionPickers')
      window.dispatchEvent(eventClose)
    }
  }

  const handleReactionClick = (emoji: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onEmojiSelect(emoji)
  }

  const handleOpenFullPicker = (e: React.MouseEvent) => {
    e.stopPropagation()
    openFullPicker()
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        const eventClose = new CustomEvent('closeAllReactionPickers')
        window.dispatchEvent(eventClose)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <Box ref={pickerRef}>
      <Flex
        backgroundColor="white"
        padding="8px"
        borderRadius="20px"
        style={{ backdropFilter: 'blur(100px)', gap: '8px' }}
        flexDirection="row"
        opacity={0.95}
        alignItems="center"
        justifyContent="center"
        boxShadow="md"
        cursor="pointer"
        ref={triggerRef}
      >
        <Box display={isFullPickerOpen ? 'none' : 'flex'} style={{ gap: '8px' }}>
          {reactionEmojis.map((emoji) => (
            <PseudoBox
              key={emoji}
              cursor="pointer"
              fontSize="24px"
              transition="transform 0.2s ease-in-out"
              _hover={{ transform: 'scale(1.3)' }}
              onClick={(e) => handleReactionClick(emoji, e)}
            >
              {emoji}
            </PseudoBox>
          ))}
        </Box>
        <Popover
          isOpen={isFullPickerOpen}
          onClose={() => {
            const eventClose = new CustomEvent('closeAllReactionPickers')
            window.dispatchEvent(eventClose)
          }}
          placement="auto"
          closeOnBlur={true}
        >
          <PopoverTrigger>
            <PseudoBox
              cursor="pointer"
              fontSize="20px"
              transition="transform 0.2s ease-in-out"
              _hover={{ transform: 'rotate(90deg) scale(1.3)' }}
              onClick={handleOpenFullPicker}
            >
              <PlusIcon />
            </PseudoBox>
          </PopoverTrigger>
          <PopoverContent
            padding={0}
            width="280px"
            border="none"
            backgroundColor="#fff"
            borderRadius="10px"
            overflow="hidden"
            boxShadow="0 4px 12px rgba(0,0,0,0.15)"
            style={{ backdropFilter: 'blur(100px)' }}
            zIndex={1000}
            _focus={{ outline: 'none', boxShadow: 'none' }}
          >
            {isEmojiPickerReady && (
              <EmojiPicker
                onEmojiClick={handleEmojiClick}
                lazyLoadEmojis={true}
                width={280}
                height={350}
                previewConfig={{ showPreview: false }}
                autoFocusSearch={false}
                searchDisabled={true}
                emojiStyle={EmojiStyle.NATIVE}
                emojiVersion="14.0"
              />
            )}
          </PopoverContent>
        </Popover>
      </Flex>
    </Box>
  )
})
